import React, { useCallback, useId } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import Input from '../../../../components/ui/Input';
import { useInlineEdit } from '../../../../components/shared/InlineEditable';
import { Task } from '../../../../types';
import { isTemporaryRemoteMediaUrl } from '../../../../utils/failedImageCache';

interface TaskItemProps {
    task: Task;
    onToggleCompletion: (taskId: string) => void;
    onUpdate: (task: Task) => void;
    onDelete: (taskId: string) => void;
    onDuplicate: (task: Task) => void;
}

const iconBtn =
    'inline-flex size-11 shrink-0 items-center justify-center rounded-md transition-colors pointer-fine:size-8';

export const TaskItem: React.FC<TaskItemProps> = ({
    task,
    onToggleCompletion,
    onUpdate,
    onDelete,
    onDuplicate,
}) => {
    const inputId = useId();

    const toDisplayString = useCallback((title: string) => title, []);

    const parse = useCallback((raw: string) => {
        const trimmed = raw.trim();
        return trimmed.length > 0 ? trimmed : null;
    }, []);

    const onCommit = useCallback(
        (title: string) => {
            onUpdate({ ...task, title });
        },
        [task, onUpdate],
    );

    const edit = useInlineEdit({
        value: task.title,
        toDisplayString,
        parse,
        onCommit,
    });

    return (
        <article
            className={`rounded-lg border bg-surface p-3 transition-colors ${
                task.isCompleted
                    ? 'border-border bg-surface-subtle/50'
                    : 'border-border hover:border-primary/30'
            }`}
        >
            {edit.isEditing ? (
                <div className="space-y-2">
                    <label htmlFor={inputId} className="sr-only">
                        Título da tarefa
                    </label>
                    <Input
                        ref={edit.inputRef}
                        type="text"
                        id={inputId}
                        name="task-edit-title"
                        value={edit.localValue}
                        onChange={(e) => edit.setLocalValue(e.target.value)}
                        onKeyDown={edit.handleKeyDown}
                        inputSize="sm"
                        placeholder="Título da tarefa"
                        autoComplete="off"
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={edit.cancel}
                            className="min-h-11 rounded-md px-3 text-caption text-muted transition-colors hover:bg-surface-subtle hover:text-secondary pointer-fine:min-h-9"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={edit.save}
                            className="min-h-11 rounded-md bg-primary px-3 text-caption font-medium text-primary-foreground transition-colors hover:bg-primary/90 pointer-fine:min-h-9"
                        >
                            Salvar
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-start gap-2">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleCompletion(task.id);
                            }}
                            className={`${iconBtn} mt-0.5 rounded-full border-2 ${
                                task.isCompleted
                                    ? 'border-success-600 bg-success-600 text-primary-foreground'
                                    : 'border-border text-transparent hover:border-success-600 hover:bg-success-bg'
                            }`}
                            aria-label={task.isCompleted ? 'Reabrir tarefa' : 'Concluir tarefa'}
                            aria-pressed={task.isCompleted}
                        >
                            {task.isCompleted ? (
                                <AppIcon name="check" className="size-3" strokeWidth={3} aria-hidden />
                            ) : null}
                        </button>

                        <div className="min-w-0 flex-1">
                            <p
                                className={`text-body-sm font-medium ${
                                    task.isCompleted ? 'text-muted line-through' : 'text-default'
                                }`}
                            >
                                {task.title}
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                                <time
                                    dateTime={task.dueDate}
                                    className={`text-caption tabular-nums ${
                                        task.isCompleted ? 'text-disabled' : 'text-muted'
                                    }`}
                                >
                                    {new Date(task.dueDate).toLocaleDateString('pt-BR')}
                                </time>
                                {task.assignedTo && (
                                    <div
                                        className="flex items-center gap-1"
                                        title={task.assignedTo.name}
                                    >
                                        {task.assignedTo.avatar &&
                                        !isTemporaryRemoteMediaUrl(task.assignedTo.avatar) ? (
                                            <img
                                                src={task.assignedTo.avatar}
                                                alt=""
                                                className="size-4 rounded-full"
                                                referrerPolicy="no-referrer"
                                            />
                                        ) : (
                                            <span
                                                className="inline-flex size-4 items-center justify-center rounded-full bg-primary-subtle text-[8px] font-bold text-primary"
                                                aria-hidden
                                            >
                                                {task.assignedTo.name?.charAt(0).toUpperCase()}
                                            </span>
                                        )}
                                        <span className="sr-only">
                                            Responsável: {task.assignedTo.name}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex shrink-0 items-center" role="group" aria-label="Ações da tarefa">
                        <button
                            type="button"
                            onClick={() => onToggleCompletion(task.id)}
                            className={`${iconBtn} text-success-text hover:bg-success-bg`}
                            aria-label={task.isCompleted ? 'Reabrir' : 'Concluir'}
                        >
                            <AppIcon name="check-circle" className="size-4" aria-hidden />
                        </button>
                        <button
                            type="button"
                            onClick={edit.startEditing}
                            className={`${iconBtn} text-primary hover:bg-primary/10`}
                            aria-label="Editar"
                        >
                            <AppIcon name="edit" className="size-4" aria-hidden />
                        </button>
                        <button
                            type="button"
                            onClick={() => onDuplicate(task)}
                            className={`${iconBtn} text-muted hover:bg-surface-subtle`}
                            aria-label="Duplicar"
                        >
                            <AppIcon name="duplicate" className="size-4" aria-hidden />
                        </button>
                        <button
                            type="button"
                            onClick={() => onDelete(task.id)}
                            className={`${iconBtn} text-danger-text hover:bg-danger-bg`}
                            aria-label="Excluir"
                        >
                            <AppIcon name="trash" className="size-4" aria-hidden />
                        </button>
                    </div>
                </div>
            )}
        </article>
    );
};
